// Global Types
import type {
  $FullAdminRegatta,
  $FullRegattaDay,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import momentTimezone from 'moment-timezone';
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatTime,
  getDateRange,
  getRegionByRegionSeasonId,
} from 'common.helpers/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import RegattaScheduleFields from '../../../fields/RegattaScheduleFields';

// Services
import Services from '../../../../services';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  id: string;
  item: $FullAdminRegatta;
  timezone: string;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'RegattaSchedule'>>;
  loading: boolean;
  resourceData: Array<$FullRegattaDay>;
};

const dateFormat: string = 'YYYY-MM-DD';

class RegattaSchedulesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      resourceData: [],
    };
  }

  componentDidMount(): void {
    this.getData();
    this.getResourceData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
      this.getResourceData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaSchedule') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });

    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaDay') {
          this.getResourceData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaSchedule({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async getResourceData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const resourceData = await Services.Api.getResourceRegattaDay({
          RegattaId,
        });

        await this.setStateAsync({
          loading: false,
          resourceData,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async stopLoader(): Promise<void> {
    await this.setStateAsync({
      loading: false,
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    return (
      <div className={'bg-white rounded border-thin-bottom'}>
        <SectionHeader
          iconClass={'icon-clock'}
          rightButtons={(
            <Button
              color={'secondary'}
              href={getDocumentUrl({
                cdnUrl: config.CDN_URL,
                docRoute: docRoute.adminRegattaSchedule,
                docType: docType.pdf,
                id: item.id,
                language: i18next.language as $Language,
              })}
              id={'regatta-schedule-file-button'}
              outline
              size={'sm'}
              target={'_blank'}
            >
              <em className={'far fa-file-pdf mr-1'} />
              {t('Regatta schedule')}
            </Button>
          )}
          title={t('Regatta schedule')}
        />
      </div>
    );
  }

  renderButtons(dataItem: $Model<'RegattaSchedule'>) {
    const {
      t,
      timezone,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    const {
      id,
    } = dataItem;

    const range = getDateRange({
      data: resourceData.map((regattaDay: $FullRegattaDay) => regattaDay.NameDate),
      extents: true,
      timezone,
    });

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaScheduleFields
              item={dataItem}
              range={range}
            />
          )}
          id={`update-regatta-schedule-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaSchedule'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaSchedule(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta schedule event')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-schedule-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-schedule-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaSchedule(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta schedule event')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-schedule-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable(filteredData: Array<$Model<'RegattaSchedule'>>, timeFormat: string) {
    return filteredData.map((dataItem: $Model<'RegattaSchedule'>) => {
      let time: string = formatTime({
        timeFormat,
        value: dataItem.StartDate,
      });

      if (dataItem.EndDate) {
        time = `${time} - ${formatTime({
          timeFormat,
          value: dataItem.EndDate,
        })}`;
      }

      return (
        <tr key={dataItem.id}>
          <td>
            {time}
          </td>
          <td className={'lh-1'}>
            <div>
              {dataItem.Name}
            </div>
            <small className={'text-muted'}>
              {dataItem.Content}
            </small>
          </td>
          <td>
            {this.renderButtons(dataItem)}
          </td>
        </tr>
      );
    });
  }

  renderRegattaDay(regattaDay: $FullRegattaDay, regattaDayMoment: moment.Moment, index: number) {
    const {
      item,
      t,
      timezone,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    const RegattaId = item.id;

    const range = getDateRange({
      data: resourceData.map(({
        NameDate,
      }: $FullRegattaDay) => NameDate),
      extents: true,
      timezone,
    });

    const newItem: $ModelUpsertParams<'RegattaSchedule'> = {
      Content: null,
      EndDate: null,
      Name: '',
      RegattaId,
      StartDate: regattaDayMoment.toDate(),
    };

    const weekDay: string = regattaDayMoment.format('ddd');

    return (
      <tr className={'bg-hightlight'}>
        <td className={'py-2'}>
          <div className={'d-flex justify-content-start align-items-center'}>
            <div className={'h2 mb-0 mr-2'}>
              {index + 1}
            </div>
            <div>
              <h5 className={'mb-0'}>
                {` ${t('Regatta day')}`}
              </h5>
              <small className={'d-block text-muted lh-1 mt-1'}>
                <span className={'text-bold'}>{weekDay}</span>
                <span>{` | ${regattaDay.NameDate}`}</span>
              </small>
            </div>
          </div>
        </td>
        <td
          className={'text-right py-2'}
          colSpan={3}
        >
          <FormDialog
            fields={(
              <RegattaScheduleFields
                item={newItem}
                range={range}
              />
            )}
            id={'create-regatta-schedule-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaSchedule'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaSchedule(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta schedule event')}
          >
            <Button
              color={'info'}
              id={'create-regatta-schedule-button'}
              size={'sm'}
            >
              {t('Add new day event')}
            </Button>
          </FormDialog>
        </td>
      </tr>
    );
  }

  renderRegattaDays() {
    const {
      item,
      main,
      timezone,
    } = this.props;
    const {
      data,
      resourceData,
    } = this.state;

    const region = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId: item.RegionSeasonId,
    });

    const timeFormat: string = region.TimeFormat;

    return resourceData.map((
      regattaDay: $FullRegattaDay,
      index: number,
    ) => {
      const regattaDayMoment = momentTimezone.tz(
        regattaDay.NameDate,
        dateFormat,
        timezone,
      );

      const filteredData: Array<$Model<'RegattaSchedule'>> = data.filter((
        dataItem: $Model<'RegattaSchedule'>,
      ) => regattaDayMoment.isSame(
        dataItem.StartDate,
        'day',
      ));

      return (
        <>
          <tbody>
            <tr>
              <td
                className={'border-0 p-1'}
                colSpan={100}
                style={{
                  backgroundColor: 'rgba(200,200,200,0.2)',
                }}
              />
            </tr>
          </tbody>
          <tbody>
            {this.renderRegattaDay(
              regattaDay,
              regattaDayMoment,
              index,
            )}
          </tbody>
          <tbody>
            {this.renderTable(
              filteredData,
              timeFormat,
            )}
          </tbody>
        </>
      );
    });
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        <Table responsive>
          {this.renderRegattaDays()}
        </Table>
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaSchedulesSectionView;
